import { type HTMLAttributes, type Ref, useId } from "react";
import { Button } from "../../atoms/Button";
import { Input } from "../../atoms/Input";
import { Label } from "../../atoms/Label";
import { cn } from "../../utils";

export type ProfileEditField = "name" | "department" | "phoneNumber";

export interface ProfileEditCardProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  "onSubmit" | "onChange"
> {
  ref?: Ref<HTMLDivElement>;
  values: Record<ProfileEditField, string>;
  maxLengths: Record<ProfileEditField, number>;
  details?: { label: string; value: string }[];
  errors?: Partial<Record<ProfileEditField, string>>;
  onChange: (field: ProfileEditField, value: string) => void;
  onSubmit: () => void;
  isLoading?: boolean;
  canSubmit?: boolean;
}

const FIELDS: {
  field: ProfileEditField;
  label: string;
  required?: boolean;
  type?: string;
  autoComplete: string;
}[] = [
  { field: "name", label: "이름", required: true, autoComplete: "name" },
  { field: "department", label: "부서", autoComplete: "organization" },
  { field: "phoneNumber", label: "연락처", type: "tel", autoComplete: "tel" },
];

function ProfileEditCard({
  className,
  ref,
  values,
  maxLengths,
  details,
  errors = {},
  onChange,
  onSubmit,
  isLoading = false,
  canSubmit = true,
  ...props
}: ProfileEditCardProps) {
  const baseId = useId();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit();
  };

  return (
    <div ref={ref} className={cn("w-full space-y-4", className)} {...props}>
      {details && (
        <dl className="divide-y divide-[var(--border-subtle)] border-y border-[var(--border-subtle)]">
          {details.map(({ label, value }) => (
            <div key={label} className="flex items-baseline justify-between gap-4 py-2">
              <dt className="flex-none text-[0.8125rem] text-[var(--text-tertiary)]">{label}</dt>
              <dd className="min-w-0 truncate text-[0.8125rem] font-semibold text-[var(--text-primary)]">
                {value}
              </dd>
            </div>
          ))}
        </dl>
      )}

      <form onSubmit={handleSubmit} noValidate className="space-y-4">
        {FIELDS.map(({ field, label, required, type, autoComplete }) => {
          const inputId = `${baseId}-${field}`;
          const errorId = `${inputId}-error`;
          const error = errors[field];
          return (
            <div key={field} className="flex flex-col">
              <Label
                htmlFor={inputId}
                required={required}
                className="mb-1.5 text-[var(--text-tertiary)]"
              >
                {label}
              </Label>
              <Input
                id={inputId}
                type={type}
                autoComplete={autoComplete}
                value={values[field]}
                maxLength={maxLengths[field]}
                onChange={(e) => onChange(field, e.target.value)}
                disabled={isLoading}
                error={!!error}
                aria-invalid={!!error}
                aria-describedby={error ? errorId : undefined}
                className="h-9 rounded-lg"
              />
              {error && (
                <p id={errorId} className="mt-1 text-xs text-error-500">
                  {error}
                </p>
              )}
            </div>
          );
        })}

        <Button type="submit" disabled={isLoading || !canSubmit} className="w-full">
          {isLoading ? "저장 중…" : "저장"}
        </Button>
      </form>
    </div>
  );
}

export { ProfileEditCard };
